import { aabbFromPoints, aabbUnion, obbToAabb, type Aabb2 } from '@indusketch/geometry';
import type { EntityCollection, ProjectDocument } from '../types.js';
import { ridgeHeight } from './roof.js';
import { clearanceObb, objectObb } from '../objects.js';
import { parkingCorners } from '../outdoor.js';
import { objectBase, RAILING_HEIGHT, stairFootprint, stairSideExit, wallBase } from '../office.js';

export interface ElementBox {
  readonly collection: EntityCollection;
  readonly id: string;
  readonly box: Aabb2;
}

/** Planar extent of each element that "zoom extents" frames. */
export function elementBoxes(doc: ProjectDocument): ElementBox[] {
  const out: ElementBox[] = [];
  const add = (collection: EntityCollection, id: string, box: Aabb2 | null) => {
    if (box) out.push({ collection, id, box });
  };
  for (const s of Object.values(doc.sites)) add('sites', s.id, aabbFromPoints(s.boundary));
  for (const b of Object.values(doc.buildings)) {
    add(
      'buildings',
      b.id,
      obbToAabb({ center: b.center, width: b.width, depth: b.depth, rotation: b.rotation }),
    );
  }
  for (const w of Object.values(doc.walls)) add('walls', w.id, aabbFromPoints([w.start, w.end]));
  for (const z of Object.values(doc.zones)) add('zones', z.id, aabbFromPoints(z.polygon));
  for (const r of Object.values(doc.rooms)) add('rooms', r.id, aabbFromPoints(r.polygon));
  for (const o of Object.values(doc.objects))
    add('objects', o.id, obbToAabb(clearanceObb(o) ?? objectObb(o)));
  for (const m of Object.values(doc.measurements))
    add('measurements', m.id, aabbFromPoints([m.start, m.end]));
  for (const r of Object.values(doc.roads)) {
    const box = aabbFromPoints(r.points);
    const h = r.width / 2;
    add(
      'roads',
      r.id,
      box && {
        min: { x: box.min.x - h, y: box.min.y - h },
        max: { x: box.max.x + h, y: box.max.y + h },
      },
    );
  }
  for (const f of Object.values(doc.fences)) add('fences', f.id, aabbFromPoints(f.points));
  for (const p of Object.values(doc.parkings))
    add('parkings', p.id, aabbFromPoints(parkingCorners(p)));
  for (const s of Object.values(doc.stairs))
    add(
      'stairs',
      s.id,
      aabbFromPoints([...stairFootprint(doc, s), ...(stairSideExit(doc, s) ?? [])]),
    );
  return out;
}

/** Planar extent of everything in the project; used for "zoom extents" and PDF framing. */
export function computeDocumentBounds(doc: ProjectDocument): Aabb2 | null {
  return aabbUnion(elementBoxes(doc).map((e) => e.box));
}

/** What distances to stray elements are measured from. */
export type FarReference = 'sites' | 'buildings' | 'center';

export interface FarElement {
  readonly collection: EntityCollection;
  readonly id: string;
  /** Gap between the element and the reference area. */
  readonly distance: number;
}

const gap = (a: Aabb2, b: Aabb2): number =>
  Math.hypot(
    Math.max(0, a.min.x - b.max.x, b.min.x - a.max.x),
    Math.max(0, a.min.y - b.max.y, b.min.y - a.max.y),
  );

function median(values: number[]): number {
  const s = [...values].sort((p, q) => p - q);
  return s[Math.floor(s.length / 2)] ?? 0;
}

/**
 * Elements lying outside the main area – the parcels, without any the buildings, without
 * those the middle of everything – farthest first, e.g. something dropped far off by mistake.
 */
export function farElements(
  doc: ProjectDocument,
  minDistance = 1,
): { reference: FarReference; elements: FarElement[] } {
  const all = elementBoxes(doc);
  const reference: FarReference =
    Object.keys(doc.sites).length > 0
      ? 'sites'
      : Object.keys(doc.buildings).length > 0
        ? 'buildings'
        : 'center';
  let area: Aabb2 | null;
  if (reference === 'center') {
    // The median ignores the stray elements themselves.
    const x = median(all.map((e) => (e.box.min.x + e.box.max.x) / 2));
    const y = median(all.map((e) => (e.box.min.y + e.box.max.y) / 2));
    area = { min: { x, y }, max: { x, y } };
  } else {
    area = aabbUnion(all.filter((e) => e.collection === reference).map((e) => e.box));
  }
  if (!area) return { reference, elements: [] };
  const elements = all
    .filter((e) => e.collection !== reference)
    .map((e) => ({ collection: e.collection, id: e.id, distance: gap(e.box, area) }))
    .filter((e) => e.distance >= minDistance)
    .sort((a, b) => b.distance - a.distance);
  return { reference, elements };
}

/** Tallest element including roof ridges, so camera framing covers the whole envelope. */
export function computeDocumentMaxHeight(doc: ProjectDocument): number {
  let max = 0;
  for (const b of Object.values(doc.buildings))
    max = Math.max(max, ridgeHeight(b) + (b.office?.usableTop ? RAILING_HEIGHT : 0));
  for (const w of Object.values(doc.walls)) max = Math.max(max, wallBase(doc, w) + w.height);
  for (const o of Object.values(doc.objects)) {
    max = Math.max(max, objectBase(doc, o) + o.dimensions.height);
  }
  return max;
}
